import React from "react";
import { useTranslation } from "react-i18next";
import { Menu } from "@mantine/core";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { useOrderStore } from "@/stores/ordersStore";
import styles from "./styles.module.css";

/**
 * The columns the backend can sort orders on (see the sort column allow-list in the application
 * repo's OrderRepository). Any other value silently falls back to `created`, so only these are offered.
 *
 * Price is left out on purpose: the allow-list has `price` and `priceInNOK`, but neither is per unit
 * (300 NOK/tonn ranks above 126 NOK/kg), so a price sort looks wrong. Add it back once the backend can
 * sort on a price normalised to one unit.
 */
export const ORDER_SORT_FIELDS = ["created", "amount"] as const;
type OrderSortField = (typeof ORDER_SORT_FIELDS)[number];

const isSortField = (value: string | null): value is OrderSortField =>
  ORDER_SORT_FIELDS.includes(value as OrderSortField);

/** The store starts on "date", which the backend treats as `created`. */
export const resolveSortField = (sortBy: string): OrderSortField => (isSortField(sortBy) ? sortBy : "created");

/**
 * Says which column the list is sorted on and which way, in words. One button shows the current sort;
 * it opens a menu to change the column and the direction. Replaces an arrow-only button that flipped
 * the direction of a sort column the user could not see.
 */
export default function OrdersSort() {
  const { t } = useTranslation();
  const sortBy = useOrderStore((state) => state.sortBy);
  const setSortBy = useOrderStore((state) => state.setSortBy);
  const sortDirection = useOrderStore((state) => state.sortDirection);
  const setSortDirection = useOrderStore((state) => state.setSortDirection);
  const setPage = useOrderStore((state) => state.setPage);

  // A reversed list starts over from its first page, same as when the column changes
  const changeDirection = (direction: "ASC" | "DESC") => {
    setSortDirection(direction);
    setPage(1);
  };

  const field = resolveSortField(sortBy);
  const isDescending = sortDirection === "DESC";

  const fieldLabels: Record<OrderSortField, string> = {
    created: t("common:date"),
    amount: t("common:amount"),
  };

  const descendingLabel = field === "created" ? t("orders:sortNewestFirst") : t("orders:sortHighestFirst");
  const ascendingLabel = field === "created" ? t("orders:sortOldestFirst") : t("orders:sortLowestFirst");
  const directionLabel = isDescending ? descendingLabel : ascendingLabel;

  return (
    <Menu width={272} position="bottom-end" offset={6} closeOnItemClick={false} withinPortal>
      <Menu.Target>
        <button
          type="button"
          className={styles.trigger}
          data-testid="orders-sort-trigger"
          aria-label={`${t("orders:sortBy")}: ${fieldLabels[field]}, ${directionLabel}`}
        >
          <Text span size="sm" color="tertiary">
            {t("orders:sortBy")}
          </Text>
          <Text span size="sm" strong>
            {fieldLabels[field]}
          </Text>
          <span className={styles.directionBadge}>
            {isDescending ? "↓" : "↑"} {directionLabel}
          </span>
          <SVGAsset name="chevron-right" width={12} height={12} className={styles.chevron} />
        </button>
      </Menu.Target>

      <Menu.Dropdown className={styles.dropdown}>
        <div className={styles.sectionLabel}>{t("orders:sortBy")}</div>
        {ORDER_SORT_FIELDS.map((value) => {
          const selected = value === field;
          return (
            <Menu.Item
              key={value}
              aria-current={selected ? "true" : undefined}
              className={selected ? styles.optionSelected : styles.option}
              data-testid={`orders-sort-field-${value}`}
              leftSection={<span className={styles.radio}>{selected && <span className={styles.radioDot} />}</span>}
              onClick={() => {
                setSortBy(value);
                setPage(1);
              }}
            >
              {fieldLabels[value]}
            </Menu.Item>
          );
        })}

        <div className={styles.divider} />
        <div className={styles.sectionLabel}>{t("orders:sortDirection")}</div>
        <div className={styles.directionSwitch}>
          <button
            type="button"
            className={isDescending ? styles.directionActive : styles.directionOption}
            aria-pressed={isDescending}
            data-testid="orders-sort-descending-button"
            onClick={() => changeDirection("DESC")}
          >
            ↓ {descendingLabel}
          </button>
          <button
            type="button"
            className={!isDescending ? styles.directionActive : styles.directionOption}
            aria-pressed={!isDescending}
            data-testid="orders-sort-ascending-button"
            onClick={() => changeDirection("ASC")}
          >
            ↑ {ascendingLabel}
          </button>
        </div>
      </Menu.Dropdown>
    </Menu>
  );
}
